import type { KanmonUiState } from 'store/Kanmon';
import { EKanmonUserState } from 'types/common';
import type { TKanmonProduct } from 'types/financials';

export function getKanmonUiView(
    ui: KanmonUiState,
    hasAccount: boolean,
    products: TKanmonProduct[] = [],
    productsStatus: 'loading' | 'ready' | 'error' = 'ready',
) {
    let phase = 'unknown';
    let titleText = 'Klarity Pay Later';
    let detailText = 'View your current financing details in Klarity Pay Later.';
    let ctaText = 'View details';
    let primaryAction: 'apply' | 'show' | 'retry' = 'show';
    if (ui.status === 'error') {
        detailText = 'Unable to load Klarity Pay Later. Please retry.';
        ctaText = 'Retry';
        primaryAction = 'retry';
    } else if (ui.status === 'idle' && !hasAccount) {
        phase = 'application';
        titleText = 'Explore Klarity Pay Later';
        detailText = 'Apply for flexible funding for your practice.';
        ctaText = 'Apply now';
        primaryAction = 'apply';
    } else if (ui.status !== 'ready') {
        detailText = 'Loading your current financing status…';
        ctaText = 'Loading…';
        primaryAction = 'retry';
    } else {
        switch (ui.userState) {
            case EKanmonUserState.START_FLOW:
                phase = 'application'; ctaText = 'Continue application';
                detailText = 'Continue your Klarity Pay Later application.'; break;
            case EKanmonUserState.USER_INPUT_REQUIRED:
            case EKanmonUserState.LOAN_APPLICATION_INCOMPLETE:
                phase = 'input'; ctaText = 'Continue application';
                titleText = 'Complete your Klarity Pay Later application';
                detailText = 'Complete the requested information in Klarity Pay Later.'; break;
            case EKanmonUserState.OTHER_USER_INPUT_REQUIRED:
                phase = 'input'; ctaText = 'View details';
                titleText = 'Waiting for the primary owner';
                detailText = 'Waiting for the primary owner to finish the application.'; break;
            case EKanmonUserState.WAITING_FOR_OFFERS:
            case EKanmonUserState.IN_MANUAL_REVIEW:
                phase = 'review'; ctaText = 'Offer processing';
                titleText = 'Your application is under review';
                detailText = 'Your application is under review. You can check its progress in Klarity Pay Later.'; break;
            case EKanmonUserState.OFFER_ACCEPTED:
                phase = 'review'; ctaText = 'Finalizing your financing';
                titleText = 'Your financing is being finalized';
                detailText = 'Your offer has been accepted. Financing is awaiting completion.'; break;
            case EKanmonUserState.VIEW_OFFERS:
                phase = 'offers'; ctaText = 'View your offers';
                titleText = 'Your Klarity Pay Later offers are ready';
                detailText = 'Your Klarity Pay Later offers are ready to review.'; break;
            case EKanmonUserState.SERVICING:
                phase = 'servicing'; ctaText = 'Manage Klarity Pay Later';
                titleText = 'Manage your Klarity Pay Later financing';
                detailText = 'Manage your financing and repayments in Klarity Pay Later.'; break;
            case EKanmonUserState.NO_OFFERS_EXTENDED:
            case EKanmonUserState.OFFERS_EXPIRED:
            case EKanmonUserState.LOAN_APPLICATION_WITHDRAWN:
                phase = 'unavailable';
                detailText = 'Check your current application and available options in Klarity Pay Later.'; break;
            default:
                break;
        }
        ctaText = ui.actionMessage?.trim() || ctaText;
    }
    const canSelectPayLater = ui.status === 'ready' && ui.userState === EKanmonUserState.SERVICING
        && productsStatus === 'ready' && products.some((item) => item.productStatus === 'CURRENT');
    return {
        phase, titleText, detailText, ctaText, primaryAction, canSelectPayLater,
        showInputRequired: ui.status === 'ready' && phase === 'input' && ui.actionRequired !== false,
    };
}
